import React, { useState } from 'react';
import { Box, GridItem, Checkbox, Select, FormControl, FormErrorMessage } from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { IoChevronDown } from 'react-icons/io5';
import { FormattedError, hasErrorMessage, getErrorMessage } from '../../../../utils/FormErrorUtils';
import { JobPositionActionEnableContainer } from './HireFormTemplateSelect';
import { DialogFlowAgent } from '../../../../firebase/firestore/documents/accounts';
import { useStoreState } from '../../../../models/hooks';
import colors from '../../../../styles/colors';

interface DialogFlowProps {
  dialogFlowEnabled: boolean;
  dialogFlowAgent: DialogFlowAgent[];
  onHandleDialogFlowCheckbox: ((checked: boolean) => void) | undefined;
  onHandleAgents: ((agent: DialogFlowAgent) => void) | undefined;
  formErrors: FormattedError[];
}

function JobPositionDialogFlowTemplate({
  dialogFlowEnabled,
  dialogFlowAgent,
  onHandleDialogFlowCheckbox,
  onHandleAgents,
  formErrors,
}: DialogFlowProps) {
  const { t } = useTranslation();
  const checkboxRef = React.useRef<HTMLInputElement>(null);
  const isDialogFlowEnabled = useStoreState((s) => s.app.accounts?.configuration?.dialogFlowEnabled);
  const dialogFlowAgents = useStoreState((s) => s.app.accounts?.dialogFlowAgents);

  const [showDialogSelect, setShowDialogSelect] = useState<boolean>(false);

  React.useEffect(() => {
    setShowDialogSelect(dialogFlowEnabled);
  }, [dialogFlowEnabled]);

  return (
    <JobPositionActionEnableContainer>
      <GridItem>
        <Checkbox
          ref={checkboxRef}
          isChecked={dialogFlowEnabled}
          data-testid="enableDialogFlowCheckbox"
          onChange={/* istanbul ignore next */ (e) => onHandleDialogFlowCheckbox?.(e.target.checked)}
        >
          {t('createJobPosition:dialogflow')}
        </Checkbox>
      </GridItem>

      {isDialogFlowEnabled && showDialogSelect && (
        <GridItem>
          <FormControl isRequired isInvalid={hasErrorMessage(formErrors, 'dialogFlowAgents')}>
            <Select
              value={dialogFlowAgent?.[0]?.agentId}
              data-testid="agentSelect"
              icon={<IoChevronDown fontSize="sm" color={colors.black} />}
              onChange={
                /* istanbul ignore next */ (e) => {
                  const foundAgent = dialogFlowAgents?.find((agent) => agent.agentId === e.target.value) as DialogFlowAgent;
                  onHandleAgents?.(foundAgent);
                }
              }
              _placeholder={{ color: colors.gray[400] }}
            >
              <option value="" selected disabled>
                {t('createJobPosition:selectAgent')}
              </option>
              {dialogFlowAgents?.map((option) => (
                <option key={option.agentId} value={option.agentId}>
                  {option.displayName}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{getErrorMessage(formErrors, 'dialogFlowAgents')}</FormErrorMessage>
          </FormControl>
        </GridItem>
      )}
    </JobPositionActionEnableContainer>
  );
}

// eslint-disable-next-line react/display-name
const DialogFlowSelect = React.memo((props: DialogFlowProps) => {
  const isDialogFlowEnabled = useStoreState((s) => s.app.accounts?.configuration?.dialogFlowEnabled);

  if (!isDialogFlowEnabled) return null;
  return (
    <Box mb={5} data-testid="job-position-dialogflow-enabled">
      <JobPositionDialogFlowTemplate {...props} />
    </Box>
  );
});

export default DialogFlowSelect;
